<template>
  <div>
    <div class="title-container">
      <h5>{{ checkSchema.name }}</h5>
      <font-awesome-icon
        :id="getInfoIconId"
        v-tooltip="checkSchema.description"
        class="check-info-icon"
        icon="circle-info"
      />
      <font-awesome-icon icon="trash-can" @click="emitDeleteCheck" />
    </div>
    <div class="check-properties-container">
      <check-property-editor
        v-for="property in checkSchema.checkProperties"
        :key="property.name"
        v-model="propertiesConfig[property.name]"
        :property-schema="property"
      />
    </div>
    <h6>{{ t('checkConfiguration.resultsConfig') }}</h6>
    <div class="check-properties-container">
      <check-results-editor
        v-for="resultCode in checkSchema.checkResults.resultCodes"
        :key="resultCode"
        v-model="resultConfigs[resultCode]"
        :check-results-schema="checkSchema.checkResults"
        :possible-values="checkSchema.checkResults.possibleValues"
        :result-code="resultCode"
      />
    </div>
    <hr />
  </div>
</template>

<script lang="ts" setup>
  import { ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { isEqual } from 'lodash';
  import CheckPropertyEditor from '@/components/CheckPropertyEditor/CheckPropertyEditor.vue';
  import CheckResultsEditor from '@/components/CheckResultsEditor/CheckResultsEditor.vue';
  import {
    CheckConfiguration,
    ICheckConfigurationDto,
    KnownCheckBotValueType,
  } from 'types';
  import {
    CheckPropertyValues,
    CheckResultValues,
  } from './CheckConfiguration.types';

  const props = defineProps<{
    checkSchema: CheckConfiguration;
    modelValue?: ICheckConfigurationDto;
  }>();
  const emit = defineEmits<{
    (e: 'update:modelValue', payload: ICheckConfigurationDto): void;
    (e: 'delete', payload: string): void;
  }>();
  const { t } = useI18n();

  const propertiesConfig = ref<CheckPropertyValues>({});
  const resultConfigs = ref<CheckResultValues>({});

  const getInfoIconId = () => `tooltip-target-${props.checkSchema.name}`;

  const emitCheckIfChanged = () => {
    const check: ICheckConfigurationDto = {
      name: props.checkSchema.name,
      params: Object.entries(propertiesConfig.value).map(([name, value]) => ({
        name,
        value: value as KnownCheckBotValueType,
      })),
      results: Object.entries(resultConfigs.value).map(
        ([resultCode, status]) => ({
          resultCode,
          status: status as string,
        }),
      ),
    };
    if (isEqual(check, props.modelValue)) return;
    else emit('update:modelValue', check);
  };

  const emitDeleteCheck = () => {
    emit('delete', props.checkSchema.name);
  };

  watch(propertiesConfig, emitCheckIfChanged, { deep: true });
  watch(resultConfigs, emitCheckIfChanged, { deep: true });
  watch(
    () => props.modelValue,
    (newValue) => {
      propertiesConfig.value = (newValue?.params ?? []).reduce(
        (acc, param) => ({
          ...acc,
          [param.name]: param.value,
        }),
        {},
      );
      resultConfigs.value = (newValue?.results ?? []).reduce(
        (acc, result) => ({
          ...acc,
          [result.resultCode]: result.status,
        }),
        {},
      );
    },
    { deep: true, immediate: true },
  );
</script>

<style lang="scss" scoped>
  .title-container {
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    column-gap: 1rem;
    h5 {
      margin: 0;
    }
    .check-info-icon {
      cursor: help;
      height: 1rem;
    }
    .fa-trash-can {
      cursor: pointer;
      height: 0.8rem;
      &:hover {
        color: $brick-red;
      }
    }
  }

  .check-properties-container {
    margin-bottom: 1rem;
    display: flex;
    column-gap: 1.5rem;
    flex-wrap: wrap;
    row-gap: 1rem;
  }
  hr {
    margin: 1rem;
  }
</style>
